import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, MessageCircle } from "lucide-react";
import Logo from "@/components/layout/Logo";
import { siteConfig } from "@/config/site.config";
import { buildWhatsAppUrl, defaultEnquiryMessage } from "@/utilities/whatsapp";
import { trackEvent, AnalyticsEvents } from "@/utilities/analytics";

const columns = {
    quick: [
        { label: "Home", href: "/" },
        { label: "About Us", href: "/about-us" },
        { label: "Our Containers", href: "/containers" },
        { label: "Blogs", href: "/blogs" },
        { label: "Contact Us", href: "/contact-us" },
    ],
    categories: [
        { label: "New Containers", href: "/containers/new-containers" },
        { label: "Used Containers", href: "/containers/used-containers" },
        { label: "Customised Containers", href: "/containers/customised-containers" },
        { label: "Storage Containers", href: "/containers/storage-containers" },
        { label: "Office Containers", href: "/containers/office-containers" },
        { label: "Sanitary Containers", href: "/containers/sanitary-containers" },
    ],
};

export default function Footer() {
    const year = new Date().getFullYear();
    const whatsappHref = buildWhatsAppUrl(defaultEnquiryMessage);
    return (
        <footer
            className="bg-cs-charcoal text-white/80 cs-corrugated relative overflow-hidden"
            data-testid="site-footer"
        >
            <div className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-cs-orange/0 via-cs-orange to-cs-orange/0" />
            <div className="cs-container py-16 md:py-20 grid gap-12 lg:grid-cols-[1.4fr_1fr_1fr_1.3fr]">
                <div>
                    <Logo variant="dark" />
                    <p className="mt-6 text-sm text-white/70 max-w-sm leading-relaxed">
                        New, used and customised container solutions for storage, cargo, workspaces, site
                        facilities and special applications in Singapore.
                    </p>
                    <div className="mt-6 flex flex-wrap gap-2">
                        <span className="cs-chip !bg-transparent !border-white/25 !text-white/70">Since 2010</span>
                        <span className="cs-chip !bg-transparent !border-white/25 !text-white/70">UEN {siteConfig.uen}</span>
                    </div>
                </div>
                <div>
                    <h3 className="font-mono text-[11px] uppercase tracking-[0.28em] text-cs-orange mb-5">
                        Quick Links
                    </h3>
                    <ul className="space-y-3">
                        {columns.quick.map((l) => (
                            <li key={l.href}>
                                <Link
                                    to={l.href}
                                    className="text-sm text-white/75 hover:text-white transition-colors"
                                    data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                >
                                    {l.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>
                <div>
                    <h3 className="font-mono text-[11px] uppercase tracking-[0.28em] text-cs-orange mb-5">
                        Container Categories
                    </h3>
                    <ul className="space-y-3">
                        {columns.categories.map((l) => (
                            <li key={l.href}>
                                <Link
                                    to={l.href}
                                    className="text-sm text-white/75 hover:text-white transition-colors"
                                    data-testid={`footer-category-${l.href.split("/").pop()}`}
                                >
                                    {l.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>
                <div>
                    <h3 className="font-mono text-[11px] uppercase tracking-[0.28em] text-cs-orange mb-5">
                        Contact
                    </h3>
                    <ul className="space-y-4 text-sm">
                        <li>
                            <a
                                href={`tel:${siteConfig.mobileRaw}`}
                                onClick={() =>
                                    trackEvent(AnalyticsEvents.PHONE_CLICK, { source: "footer" })
                                }
                                className="inline-flex items-start gap-3 text-white/85 hover:text-white transition-colors"
                                data-testid="footer-phone-link"
                            >
                                <Phone size={16} strokeWidth={2} className="mt-0.5 text-cs-orange" />
                                <span className="font-mono">{siteConfig.mobile}</span>
                            </a>
                        </li>
                        <li>
                            <a
                                href={`mailto:${siteConfig.email}`}
                                onClick={() =>
                                    trackEvent(AnalyticsEvents.EMAIL_CLICK, { source: "footer" })
                                }
                                className="inline-flex items-start gap-3 text-white/85 hover:text-white transition-colors break-all"
                                data-testid="footer-email-link"
                            >
                                <Mail size={16} strokeWidth={2} className="mt-0.5 text-cs-orange" />
                                <span>{siteConfig.email}</span>
                            </a>
                        </li>
                        <li className="inline-flex items-start gap-3 text-white/85">
                            <MapPin size={16} strokeWidth={2} className="mt-0.5 text-cs-orange" />
                            <address className="not-italic leading-relaxed">
                                {siteConfig.address.line1}
                                <br />
                                {siteConfig.address.line2} {siteConfig.address.unit}
                                <br />
                                {siteConfig.address.postal}
                            </address>
                        </li>
                    </ul>
                    <a
                        href={whatsappHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() =>
                            trackEvent(AnalyticsEvents.WHATSAPP_CLICK, { source: "footer" })
                        }
                        className="cs-btn-whatsapp mt-6 w-full text-xs"
                        data-testid="footer-whatsapp-btn"
                    >
                        <MessageCircle size={16} strokeWidth={2} />
                        WhatsApp Us
                    </a>
                </div>
            </div>

            <div className="border-t border-white/10">
                <div className="cs-container py-6 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-white/55">
                    <p className="font-mono uppercase tracking-widest text-[11px]">
                        © {year} {siteConfig.registeredCompanyName}. All Rights Reserved.
                    </p>
                    <ul className="flex items-center gap-5 font-mono uppercase tracking-widest text-[11px]">
                        <li>
                            <Link
                                to="/privacy-policy"
                                className="hover:text-white transition-colors"
                                data-testid="footer-privacy-link"
                            >
                                Privacy Policy
                            </Link>
                        </li>
                        <li>
                            <Link
                                to="/terms-of-use"
                                className="hover:text-white transition-colors"
                                data-testid="footer-terms-link"
                            >
                                Terms of Use
                            </Link>
                        </li>
                        <li>
                            <Link
                                to="/sitemap"
                                className="hover:text-white transition-colors"
                                data-testid="footer-sitemap-link"
                            >
                                Sitemap
                            </Link>
                        </li>
                    </ul>
                </div>
            </div>
        </footer>
    );
}
